import { Stack, TableCell, TableRow } from '@mui/material'
import React from 'react'
import { Link, NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import { formatBigMoney, formatDateYYYYMMDD, formatNumber } from '../../../../packages/utils/numberFormatters'
import { HasWarrantLabel } from '../../../../components/entities/IssueTypeLabels'
import { normalizedWarrantName } from '@finance-lab/utils/shared'
import { WarrantListItem } from '@finance-lab/utils/types'

export const WarrantHistoryTableRow = ({
    date,
    warrant,
    locale,
    multipleInstrumentsExists = false,
    splitAdjust = false,
    isSmallScreen,
}: {
    date: Date
    warrant: WarrantListItem
    locale: string
    multipleInstrumentsExists: boolean
    splitAdjust: boolean
    isSmallScreen: boolean
}) => {
    const { t } = useTranslation()

    let sumSharesSubscribedRow = null
    let warrantSubTotalRow = null

    for (const sp of warrant.subscriptionPeriods) {
        if (sp.sharesSubscribed !== null) {
            const adjustedSharesSubscribed = sp.sharesSubscribed / (splitAdjust ? sp.splitAdjustment : 1)
            sumSharesSubscribedRow = (sumSharesSubscribedRow ?? 0) + adjustedSharesSubscribed
        }

        if (sp.sharesSubscribed !== null && sp.warrantSubPrice !== null) {
            const adjustedSharesSubscribed = sp.sharesSubscribed / (splitAdjust ? sp.splitAdjustment : 1)
            const adjustedSubPrice = sp.warrantSubPrice / (splitAdjust ? sp.splitAdjustment : 1)
            warrantSubTotalRow = (warrantSubTotalRow ?? 0) + (adjustedSubPrice ?? 0) * adjustedSharesSubscribed
        }
    }

    const hasTotalGuarantorAllocation = warrant.totalGuarantorAllocation !== null
    const hasTotalGuarantorRemunerationShares = warrant.totalGuarantorRemunerationShares !== null

    return (
        <>
            <TableRow
                sx={{
                    fontStyle: warrant.cancelled ? 'italic' : 'normal',
                    '& .MuiTableCell-root': {
                        ...(hasTotalGuarantorAllocation || hasTotalGuarantorRemunerationShares
                            ? {
                                  borderBottom: 0,
                              }
                            : {}),
                    },
                }}
            >
                <TableCell>{formatDateYYYYMMDD(date, { locale })}</TableCell>
                {multipleInstrumentsExists && !isSmallScreen ? <TableCell>{warrant.instrumentSymbol}</TableCell> : null}
                <TableCell>
                    <Stack direction='row' spacing={2}>
                        <HasWarrantLabel />
                        <Link component={NavLink} to={`/warrants/${warrant.id}`}>
                            {normalizedWarrantName(warrant)}
                        </Link>
                    </Stack>
                </TableCell>
                {!isSmallScreen ? (
                    <>
                        <TableCell>
                            {warrantSubTotalRow !== null && sumSharesSubscribedRow !== null
                                ? formatNumber(warrantSubTotalRow / sumSharesSubscribedRow, {
                                      locale,
                                      minDecimals: 2,
                                      maxDecimals: 5,
                                  })
                                : '-'}
                        </TableCell>
                        <TableCell>
                            {warrant.cancelled
                                ? t('pages.warrants.status.cancelled')
                                : sumSharesSubscribedRow === null
                                  ? t('site.notCommunicatedYet')
                                  : formatNumber(sumSharesSubscribedRow, {
                                        locale,
                                        decimals: 0,
                                    })}
                        </TableCell>
                    </>
                ) : null}
                <TableCell>
                    {warrantSubTotalRow !== null
                        ? formatBigMoney(warrantSubTotalRow, { locale, decimals: 0, tooltip: true })
                        : '-'}
                </TableCell>
            </TableRow>

            {hasTotalGuarantorAllocation && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(hasTotalGuarantorRemunerationShares
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('pages.companies.history.directedIssueToGuarantors')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(warrant.totalGuarantorAllocation, { locale, decimals: 0 })}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {formatBigMoney(warrant.totalGuarantorAllocationSize, { locale, decimals: 0, tooltip: true })}
                    </TableCell>
                </TableRow>
            )}

            {hasTotalGuarantorRemunerationShares && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>{t('pages.warrants.guarantorRemunerationShares')}</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(warrant.totalGuarantorRemunerationShares, { locale, decimals: 0 })}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {formatBigMoney(warrant.totalGuarantorRemunerationSharesSize, {
                            locale,
                            decimals: 0,
                            tooltip: true,
                        })}
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}
